import { Bot, CircleCheck, Cloud, Database, Eye, FileSearch, Filter, Folder, Link } from "lucide-react";
import type { DocumentTypeSchema } from "@noa/api-core";
import { useTranslation } from "react-i18next";
import { getExtractionSource, isPathExtracted, type CustomJobForm } from "~/utils/custom-job-form";
import { Card, Text, Tooltip } from "~/components/ui";

type CustomJobPreviewProps = {
  values: CustomJobForm;
  sourceLabel: string;
  parserLabel: string | undefined;
  selectedSchema: DocumentTypeSchema | undefined;
};

export function CustomJobPreview({ values, sourceLabel, parserLabel, selectedSchema }: CustomJobPreviewProps) {
  const { t } = useTranslation("admin");

  const pathFields = selectedSchema?.fields?.filter(isPathExtracted) ?? [];
  const llmFields = selectedSchema?.fields?.filter((f) => !isPathExtracted(f)) ?? [];

  return (
    <Card size="small" className="lg:col-span-2 h-fit" title={t("uploadAdmin.customJob.preview")}>
      <div className="flex flex-col gap-3 text-sm">
        {/* Source + Pattern row */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <Cloud size={14} className="text-blue-500" />
            <Text strong>{sourceLabel}</Text>
          </div>
          <div className="flex items-center gap-1.5">
            <Folder size={14} className="text-yellow-500" />
            <code className="bg-noa-hover px-1.5 py-0.5 rounded">{values.filesPattern || "*"}</code>
          </div>
        </div>

        {/* Schema details */}
        {selectedSchema ? (
          <div className="border border-noa rounded p-3 bg-noa-hover">
            <div className="flex items-center gap-2 mb-2">
              <Database size={14} className="text-purple-500" />
              <Text strong>{selectedSchema.schema_id}</Text>
              <span className="text-xs text-noa-muted">({selectedSchema.doc_type})</span>
            </div>
            {selectedSchema.description && (
              <Text tone="muted" className="text-xs block mb-2">
                {selectedSchema.description}
              </Text>
            )}

            {/* Path-extracted fields */}
            {pathFields.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-1.5 text-xs text-noa-muted mb-2">
                  <Link size={14} />
                  <span>{t("uploadAdmin.customJob.pathFields")}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {pathFields.map((field) => {
                    const pattern = getExtractionSource(field);
                    return (
                      <Tooltip
                        key={field.name}
                        title={
                          <div>
                            <div className="font-medium">{field.name}</div>
                            <div className="text-xs opacity-80">Type: {field.type || "string"}</div>
                            <code className="text-xs block mt-1 bg-black/30 px-1 py-0.5 rounded">{pattern}</code>
                          </div>
                        }
                      >
                        <div className="flex items-center gap-2 text-xs bg-noa-inset border border-noa rounded px-2 py-1.5 cursor-help">
                          <span className="font-medium text-blue-400">{field.name}</span>
                          <span className="text-noa-muted text-[10px]">{field.type}</span>
                          <code className="text-[10px] bg-blue-500/10 text-blue-300 px-1.5 py-0.5 rounded ml-auto truncate max-w-24">
                            {pattern}
                          </code>
                        </div>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            )}

            {/* LLM-extracted fields */}
            {llmFields.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-1.5 text-xs text-noa-muted mb-2">
                  <Bot size={14} />
                  <span>{t("uploadAdmin.customJob.llmFields")}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {llmFields.map((field) => (
                    <Tooltip
                      key={field.name}
                      title={
                        <div>
                          <div className="font-medium">{field.name}</div>
                          <div className="text-xs opacity-80">Type: {field.type || "string"}</div>
                          {field.required && <div className="text-xs text-purple-300 mt-0.5">Required</div>}
                        </div>
                      }
                    >
                      <span
                        className={`text-xs px-2 py-1 rounded cursor-help ${
                          field.required
                            ? "bg-purple-500/20 text-purple-400"
                            : "bg-noa-inset text-noa-muted border border-noa"
                        }`}
                      >
                        {field.name}
                        {field.required && <span className="text-purple-300 ml-0.5">*</span>}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}

            {selectedSchema.parser && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-noa-muted">
                <FileSearch size={14} />
                <span>
                  {t("uploadAdmin.customJob.defaultParser")}: {selectedSchema.parser}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-noa rounded p-3 text-center">
            <Database size={24} className="text-noa-muted mb-1" />
            <Text tone="muted" className="text-xs block">
              {t("uploadAdmin.customJob.noSchemaHint")}
            </Text>
          </div>
        )}

        {/* Options badges */}
        <div className="flex flex-wrap gap-1.5">
          {values.withVision && (
            <span className="inline-flex items-center gap-1 text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">
              <Eye size={14} /> {t("uploadAdmin.form.withVision")}
            </span>
          )}
          {values.skipExisting && (
            <span className="inline-flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">
              <CircleCheck size={14} /> {t("uploadAdmin.customJob.skipExisting")}
            </span>
          )}
          {values.limitEnabled && (
            <span className="inline-flex items-center gap-1 text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">
              <Filter size={14} /> {t("uploadAdmin.customJob.limitN", { n: values.maxRecords })}
            </span>
          )}
          {parserLabel && (
            <span className="inline-flex items-center gap-1 text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">
              <FileSearch size={14} /> {parserLabel}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
